import React, {useEffect, useState} from "react";

import CommonNoteModal from "./CommonNoteModal";
import {useCreateCannedNoteMutation} from "../../data/cannedNotes/cannedNotesApi";

interface Note {
    sort: number;
    comment: string;
    bold: boolean;
    yellowHighlight: boolean;
    redColor: boolean;
    certificateLevelNote?: boolean;
    orderId?: number;
    taxSuitLevelNote?: boolean;
    associationNote?: boolean;
}

interface Certificate {
    certificateNotes: Note[];
    accounts: {
        accountNotes: Note[];
        taxSuitNotes: Note[];
        notes: Note[];
    }[];
}

interface EditorAddEditNoteFormProps {
    title: string;
    show: boolean;
    setShow: (show: boolean) => void;
    index: number | null;
    level?: boolean;
    certificate: Certificate;
    setCertificate: (certificate: Certificate) => void;
    orderId: any;
    showAccount: number;
    setIsSavedResult: (val: boolean) => void;
    isTaxSuit?: boolean;
    isHoaNote?: boolean;
    length?: number;
    setUpdatedAccountIndexList: any;
}

export default function EditorAddEditNoteForm({
                                                  title,
                                                  show,
                                                  setShow,
                                                  index,
                                                  level,
                                                  certificate,
                                                  setCertificate,
                                                  orderId,
                                                  showAccount,
                                                  setIsSavedResult,
                                                  isTaxSuit = false,
                                                  isHoaNote = false,
                                                  length = 1,
                                                  setUpdatedAccountIndexList
                                              }: EditorAddEditNoteFormProps) {
    const [noteBody, setNoteBody] = useState<string>("");
    const [sortIndex, setSortIndex] = useState<number>(length);
    const [isBold, setIsBold] = useState<boolean>(false);
    const [saveAsCanned, setSaveAsCanned] = useState<boolean | null>(null);
    const [yellowHighlight, setYellowHighlight] = useState<boolean>(false);
    const [redColor, setRedColor] = useState<boolean>(false);
    const [disable, setDisable] = useState<boolean>(false);
    const [createCannedNote] = useCreateCannedNoteMutation();

  useEffect(() => {
    if (index !== null) {
      if (!isHoaNote) {
        if (!isTaxSuit) {
          if (level) {
            const note = certificate.certificateNotes[index];
            setNoteBody(note.comment);
            setIsBold(note.bold);
            setRedColor(note.redColor);
            setYellowHighlight(note.yellowHighlight);
            setSortIndex(note.sort);
          } else {
            const note = certificate.accounts[showAccount].accountNotes[index];
            setSortIndex(note.sort);
            setNoteBody(note.comment);
            setIsBold(note.bold);
            setRedColor(note.redColor);
            setYellowHighlight(note.yellowHighlight);
          }
        } else {
          const note = certificate.accounts[showAccount].taxSuitNotes[index];
          setSortIndex(note.sort);
          setNoteBody(note.comment);
          setIsBold(note.bold);
          setRedColor(note.redColor);
          setYellowHighlight(note.yellowHighlight);
        }
      } else {
        const note = certificate.accounts[showAccount].notes[index];
        setSortIndex(note.sort);
        setNoteBody(note.comment);
        setIsBold(note.bold);
        setRedColor(note.redColor);
        setYellowHighlight(note.yellowHighlight);
      }
    }
  }, []);

    function dismissModal(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        setShow(false);
    }

  const handleSubmitNote = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!noteBody) return;

      const note: Note = {
          sort: sortIndex,
          comment: noteBody,
          bold: isBold,
          yellowHighlight,
          redColor,
          orderId
      };

      // Create a shallow copy of the certificate to avoid direct mutation
      let updatedCertificate = {...certificate};

      // Clone the nested arrays to ensure they are mutable, with fallback to empty array if undefined
      updatedCertificate = {
          ...updatedCertificate,
          accounts: updatedCertificate.accounts.map(account => ({
              ...account,
              // Ensure notes is an array (if undefined, fallback to empty array)
              notes: Array.isArray(account.notes) ? [...account.notes] : [],
              accountNotes: Array.isArray(account.accountNotes) ? [...account.accountNotes] : [],
              taxSuitNotes: Array.isArray(account.taxSuitNotes) ? [...account.taxSuitNotes] : []
          })),
          certificateNotes: Array.isArray(updatedCertificate.certificateNotes) ? [...updatedCertificate.certificateNotes] : []
      };

      // Now update the specific array based on the note type
      if (isHoaNote) {
          note.associationNote = false;
          if (index === null) {
              updatedCertificate.accounts[showAccount].notes.push(note);
          } else {
              updatedCertificate.accounts[showAccount].notes[index] = note;
          }
      } else if (!isTaxSuit) {
          note.certificateLevelNote = level;
          if (level) {
              if (index === null) {
                  updatedCertificate.certificateNotes.push(note);
              } else {
                  updatedCertificate.certificateNotes[index] = note;
              }
          } else {
              if (index === null) {
                  updatedCertificate.accounts[showAccount].accountNotes.push(note);
              } else {
                  updatedCertificate.accounts[showAccount].accountNotes[index] = note;
              }
          }
      } else {
          note.certificateLevelNote = false;
          note.taxSuitLevelNote = true;
          if (index === null) {
              updatedCertificate.accounts[showAccount].taxSuitNotes.push(note);
          } else {
              updatedCertificate.accounts[showAccount].taxSuitNotes[index] = note;
          }
      }

      setCertificate(updatedCertificate);

      setIsSavedResult(false);
      setUpdatedAccountIndexList((prev: number[]) =>
          prev.includes(showAccount) ? prev : [...prev, showAccount]
      );      setShow(false);

      if (saveAsCanned) {
          try {
              setDisable(true);
              await createCannedNote({

                      comment: noteBody,
                      bold: isBold,
                      type: "Order",
                      yellowHighlight,
                      redColor,
                      sort: sortIndex

              }).unwrap();
          } catch (error) {
              console.error("Error saving canned note:", error);
          } finally {
              setDisable(false);
          }
      }
  };

  return (
      <>

        <CommonNoteModal
          noteBody={noteBody}
          setNoteBody={setNoteBody}
          handleSubmit={handleSubmitNote}
          dismissModal={dismissModal}
          formNoteType={isHoaNote ? "HOA" : "TAX"}
          disable={disable}
          setShow={setShow}
          type={'Order'}
          title={title}
          bold={isBold}
          setBold={setIsBold}
          saveAsCanned={saveAsCanned}
          setSaveAsCanned={setSaveAsCanned}
          btnName={index !== null ? "Update" : "Add"}
          yellowHighlight={yellowHighlight}
          setYellowHighlight={setYellowHighlight}
          redColor={redColor}
          setRedColor={setRedColor}
          isCertificateNote={true}
          sort={sortIndex}
          setSort={setSortIndex}
          show={show}
          onSubmit={handleSubmitNote}
        />

      </>
  );
}